import { CircularProgress } from '@mui/material';
import {
  type Pagination,
  type PlayerTransfer,
  type PlayerTransferRelatedTransferResponseRelatedPagedData,
  rdbV1TransfersBestAvailableList,
  type RdbV1TransfersBestAvailableListParams,
  rdbV1TransfersLatestList,
  type RdbV1TransfersLatestListParams,
} from '@on3/api';
import { useAuth } from '@on3/ui-lib/index';
import { externalApi } from '@on3/ui-lib/utils/api';
import {
  ColumnHeaders,
  type ColumnOption,
} from '@public/components/Db/Filters/SortFilter';
import useLoadMore from '@public/utils/hooks/useLoadMore';
import { createKey } from '@public/utils/meta';
import { getUnixTime } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { IndustryComparisonTpHeaders } from './Header/IndustryComparisonTpHeaders';
import styles from './IndustryComparisonTpList.module.scss';
import { IndustryComparisonTpItem } from './Item';

interface IndustryComparisonListAltProps {
  playerData?: PlayerTransferRelatedTransferResponseRelatedPagedData | null;
  columns?: ColumnOption[];
  pagination?: Pagination;
  pageParams: {
    baseParams:
      | RdbV1TransfersLatestListParams
      | RdbV1TransfersBestAvailableListParams;
    bestParams: RdbV1TransfersBestAvailableListParams;
  };
}

const wireColumns: ColumnOption[] = [
  'Industry',
  'On3',
  '247',
  'Rivals',
  'date',
];

const ascendingColumns: ColumnOption[] = [
  'portalRank',
  'jerseyNumber',
  'rank',
  'lastName',
  'name',
  'age',
  'classRank',
];

export const IndustryComparisonTpList = ({
  pageParams,
  playerData,
  columns = ['portalRank', 'Industry', 'On3', '247', 'Rivals', 'date'],
}: IndustryComparisonListAltProps) => {
  const { user } = useAuth();
  const disabled = !user?.has;

  const { relatedModel, pagination } = playerData || {};

  const isWire = relatedModel?.type === 'wire';
  const listColumns = isWire ? wireColumns : columns;

  const [ascending, setAscending] = useState(isWire ? false : true);
  const [selected, setSelected] = useState<ColumnOption>(
    isWire ? 'date' : 'portalRank',
  );

  const displaySelectedObject = ColumnHeaders.find(
    (item) => item.name === selected,
  );

  const [list, setList] = useState(playerData?.list || []);
  const [page, setPage] = useState(pagination?.currentPage || 1);

  const { data, registered, processing, loadMore } = useLoadMore({
    key: page,
    register: page < (pagination?.pageCount || 1) && !disabled,
    request: isWire
      ? () =>
          rdbV1TransfersLatestList(externalApi, {
            ...pageParams.baseParams,
            page: page + 1,
          })
      : () =>
          rdbV1TransfersBestAvailableList(externalApi, {
            ...pageParams.bestParams,
            page: page + 1,
          }),
  }) as {
    data: PlayerTransferRelatedTransferResponseRelatedPagedData;
    registered: boolean;
    processing: boolean;
    loadMore: () => void;
  };

  useEffect(() => {
    if (data) {
      setList((prev) => [...prev, ...(data.list || [])]);
      setPage(data.pagination?.currentPage as number);
    }
  }, [data]);

  const getColumnSortValue = (item: PlayerTransfer, column: ColumnOption) => {
    switch (column) {
      case 'name':
      case 'lastName':
        return item.name || '';
      case 'date':
        return getUnixTime(new Date(item.commitStatus?.date || 0));
      case 'portalRank':
        return item.transferRating?.consensusNationalRank;
      default:
        return item.transferIndustryComparison?.find(
          (comp) => comp.type === column,
        )?.rating;
    }
  };

  const getColumnSecondarySort = useCallback(
    (item: PlayerTransfer, column: ColumnOption) => {
      switch (column) {
        case 'Industry':
        case 'On3':
        case '247':
        case 'Espn':
        case 'Rivals':
          return {
            value: item.transferIndustryComparison?.find(
              (comp) => comp.type === column,
            )?.overallRank,
            ascending: !ascending,
          };
        default:
          return {
            value: 0,
            ascending: true,
          };
      }
    },
    [ascending],
  );

  const sortedList = useMemo(
    () =>
      list.sort((a, b) => {
        const setA = getColumnSortValue(a, selected);
        const setB = getColumnSortValue(b, selected);

        if (!setA && setA !== 0) return 1;
        if (!setB && setB !== 0) return -1;

        if (setA === setB) {
          const setC = getColumnSecondarySort(a, selected);
          const setD = getColumnSecondarySort(b, selected);

          if (!setC.value && setC.value !== 0) return 1;
          if (!setD.value && setD.value !== 0) return -1;

          if (setC.ascending ?? ascending) {
            return setC.value > setD.value ? 1 : -1;
          }

          return setC.value < setD.value ? 1 : -1;
        }

        if (ascending) {
          return setA > setB ? 1 : -1;
        }

        return setA < setB ? 1 : -1;
      }),
    [list, selected, ascending, getColumnSecondarySort],
  );

  const handleSort = useCallback(
    (column: ColumnOption) => {
      if (column === selected) {
        setAscending(!ascending);
      } else {
        setSelected(column);
        setAscending(ascendingColumns.includes(column) ? true : false);
      }
    },
    [ascending, selected],
  );

  return (
    <div className={styles.block}>
      {sortedList?.length ? (
        <>
          <IndustryComparisonTpHeaders
            ascending={ascending}
            columns={listColumns}
            disabled={disabled}
            onChange={handleSort}
            rankingYear={playerData?.relatedModel?.year}
            selected={selected}
          />
          <div
            aria-label="Sortable Industry Comparison player list"
            aria-labelledby={displaySelectedObject?.ariaLabel}
            role="table"
          >
            <ol
              className={styles.list}
              data-sort-type={selected}
              id="player-list"
              role="rowgroup"
            >
              {sortedList.map((item, index) => (
                <IndustryComparisonTpItem
                  columns={listColumns}
                  key={createKey(
                    item.key,
                    index,
                    selected,
                    ascending ? 'asc' : 'desc',
                    'industry-comparison',
                  )}
                  listIndex={index}
                  player={item}
                  relatedModel={playerData?.relatedModel}
                  selected={selected}
                />
              ))}
            </ol>
          </div>
          {registered && (
            <div className={styles.load}>
              {processing ? (
                <CircularProgress color="primary" size="1rem" />
              ) : (
                <button onClick={loadMore} type="button">
                  Load More
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <p className={styles.noListText}>
          There are no players for this selection.
        </p>
      )}
    </div>
  );
};
